import { useEffect } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { useCallState } from "@/zustand/CallState";
import { ORION_MINIMIZE_TOOLBAR_BUTTON_ID } from "./jitsiToolbar";

// Re-exported so importers of this ID have a hook-local handle; source of truth is `jitsiToolbar.ts`.
export { ORION_MINIMIZE_TOOLBAR_BUTTON_ID };

/**
 * Payload of Jitsi's `toolbarButtonClicked` event. The `key` field carries the button id
 * (confirmed against the deployed bundle, build v=9268); `id`/`buttonId` are read defensively.
 * Mirrors `useOrionChatButtonEvent` / `useModerationButtonEvent`.
 */
type ToolbarButtonClickedEvent = {
  key?: string;
  id?: string;
  buttonId?: string;
};

/**
 * Wires the Jitsi iframe's `toolbarButtonClicked` event for the "Minimize" custom toolbar button
 * to the call's minimize action.
 *
 * Minimize used to be a floating overlay button rendered on top of the iframe; it now lives IN the
 * Jitsi bottom toolbar (via `configOverwrite.customToolbarButtons`, see `buildOrionCustomToolbarButtons`)
 * alongside the chat + moderation buttons. When clicked, Jitsi emits `toolbarButtonClicked` with the
 * button's `key`, and this listener collapses the call by setting `callMinimized`.
 *
 * The RESTORE (un-minimize) control stays an overlay — once minimized the iframe is height:0 and its
 * toolbar is hidden, so a toolbar button could never bring the call back. Restore lives in
 * `CallButtons.tsx` (the Maximize2 pill).
 *
 * Reads `useCallState.getState()` at call time (same pattern as the sibling hooks) so the listener
 * never captures a stale action reference across renders.
 */
export default function useMinimizeButtonEvent(
  apiRef: IJitsiMeetExternalApi | null,
): void {
  useEffect(() => {
    if (!apiRef) {
      return;
    }

    function handleToolbarButtonClicked(payload?: ToolbarButtonClickedEvent) {
      const clickedId = payload?.key ?? payload?.id ?? payload?.buttonId;
      if (clickedId !== ORION_MINIMIZE_TOOLBAR_BUTTON_ID) {
        return;
      }
      useCallState.getState().setCallMinimized(true);
    }

    apiRef.on("toolbarButtonClicked", handleToolbarButtonClicked);

    return () => {
      apiRef.removeListener("toolbarButtonClicked", handleToolbarButtonClicked);
    };
  }, [apiRef]);
}
